import { OrgContext } from "contexts/OrgContext";
import { InstanceTypesContext } from "contexts/InstanceTypesContext";
import React, { useContext, useMemo, useState } from "react";
import { AnimatePresence } from "framer-motion";
import ComputeSelector from "components/Environment/Create/ComputeSelector";
import { GPUInstanceType } from "types/InstanceTypes";
import { allPublicCloudsName } from "components/Environment/Create/utils";
import FilterBar from "components/Environment/Create/ComputeSelector/Advanced/FilterBar";
import { ContainerSelected } from "components/Environment/shared/BuildTypes";
import { getPrimaryInstanceCloudCred } from "utils/cloudCreds";

export interface LaunchableComputeSelect {
  selectedInstanceType: GPUInstanceType | undefined;
  setSelectedInstanceType: (instanceType: GPUInstanceType | undefined) => void;
  storage: string;
  setStorage: (storage: string) => void;
}

const LaunchableComputeSelect: React.FC<LaunchableComputeSelect> = ({
  selectedInstanceType,
  setSelectedInstanceType,
  storage,
  setStorage,
}) => {
  const orgContext = useContext(OrgContext);
  const instanceTypesContext = useContext(InstanceTypesContext);

  //environment state
  const [container, setContainer] = useState<ContainerSelected | undefined>({
    vmBuild: {
      forceJupyterInstall: false,
    },
  });
  const [pythonVersion, setPythonVersion] = useState<string>("3.10");
  const [cudaVersion, setCudaVersion] = useState<string>("12.0.1");
  const [selectedCloudName, setSelectedCloudName] =
    useState(allPublicCloudsName);
  const [loading, setLoading] = useState(false);

  const [displayGPUInstances, setDisplayGPUInstances] = useState<
    GPUInstanceType[] | undefined
  >(undefined);
  const [displayCPUInstances, setDisplayCPUInstances] = useState<
    GPUInstanceType[] | undefined
  >(undefined);
  const [persistentOnly, setPersistentOnly] = useState<boolean>(false);
  const [flexiblePortControlOnly, setFlexiblePortControlOnly] =
    useState<boolean>(false);
  const [flexibleStorageOnly, setFlexibleStorageOnly] =
    useState<boolean>(false);

  const handleSelectedInstanceChange = (
    instanceType: GPUInstanceType | undefined,
  ) => {
    setSelectedInstanceType(instanceType);

    if (instanceType) {
      const cloudCred = getPrimaryInstanceCloudCred(instanceType);
      setStorage(cloudCred?.id === "dgxc-gcp-1" ? "512" : "256");
    }
  };

  const handleCompletion = async () => {};

  const isDeployDisabled = useMemo(() => {
    return (
      instanceTypesContext.GPUInstancesAvailable.length === 0 ||
      Number(storage) < 100 ||
      Number(storage) > 16000
    );
  }, [instanceTypesContext.GPUInstancesAvailable, storage]);

  const filterForValidInstances = useMemo(() => {
    return instanceTypesContext.GPUInstancesAvailable.filter((gpu) => {
      return getPrimaryInstanceCloudCred(gpu)?.id !== "dgxc-gcp-1";
    });
  }, [instanceTypesContext.GPUInstancesAvailable]);

  return (
    <div>
      {container && !selectedInstanceType && (
        <div className="mb-3 flex justify-end">
          <FilterBar
            allGPUInstances={filterForValidInstances}
            allCPUInstances={instanceTypesContext.CPUInstancesAvailable}
            setDisplayGPUInstances={setDisplayGPUInstances}
            setDisplayCPUInstances={setDisplayCPUInstances}
            containerSelected={container}
            onFilterStateChange={(
              persistent,
              flexiblePorts,
              flexibleStorage,
            ) => {
              setPersistentOnly(persistent);
              setFlexiblePortControlOnly(flexiblePorts);
              setFlexibleStorageOnly(flexibleStorage);
            }}
          />
        </div>
      )}
      <div className="flex flex-col">
        <AnimatePresence initial={false}>
          {container && (
            <>
              <ComputeSelector
                allGPUInstances={filterForValidInstances}
                isLoadingInstances={instanceTypesContext.isInstancesLoading}
                displayGPUInstances={displayGPUInstances}
                selectedInstance={selectedInstanceType}
                setSelectedInstance={handleSelectedInstanceChange}
                setStorage={setStorage}
                storage={storage}
                containerSelected={container}
                selectedCloudName={selectedCloudName}
                hideDgxc={true}
                persistentOnly={persistentOnly}
                flexiblePortControlOnly={flexiblePortControlOnly}
                flexibleStorageOnly={flexibleStorageOnly}
                animateLoaderCarouselLayout={false}
                syncGpuSelectionToUrl={false}
                cpuInstances={
                  displayCPUInstances ??
                  instanceTypesContext.CPUInstancesAvailable
                }
              />
            </>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default LaunchableComputeSelect;
